"use client"

import React, { useState, useEffect } from 'react';
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/contexts/NotificationsContext";
import { NavigationProps } from "@/types/navigation";
import { NotificationsList } from '@/components/notifications';
import { Card } from "@/components/ui/card";
import { CheckCheck } from "lucide-react";

interface NotificationsPageProps extends NavigationProps {}

export default function NotificationsPage({ onNavigate }: NotificationsPageProps) {
  const {
    notifications,
    isLoading,
    markAsRead,
    markAsUnread,
    removeNotification,
    markAllAsRead,
  } = useNotifications();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Show 5 notifications per page
  
  // Reset to first page when notifications change
  useEffect(() => {
    setCurrentPage(1);
  }, [notifications?.length]);
  
  // Check if there are any unread notifications
  const hasUnreadNotifications = notifications?.some(notification => !notification.isRead) || false;
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Notifications</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>   
          </Breadcrumb>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
            <p className="text-muted-foreground">Stay updated with your financial activities</p>
          </div>
          {hasUnreadNotifications && (
            <Button 
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={markAllAsRead}
              disabled={isLoading}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all as read
            </Button>
          )}
        </div>

        <NotificationsList 
          notifications={notifications}
          isLoading={isLoading}
          onMarkAsRead={markAsRead}
          onMarkAsUnread={markAsUnread}
          onRemove={removeNotification}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </main>
    </>
  );
} 